import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { useDebouncedValue } from '@mantine/hooks';
import classNames from 'classnames';
import { DateTime } from 'luxon';
import { Ban, Search } from 'tabler-icons-react';

import { PalletsStackIcon } from 'assets';
import { CustomInfiniteScroll, NoItemsNotificationWithLink, StatusLegend } from 'components/shared';
import StyledButton from 'components/shared/StyledButton/StyledButton';
import StyledTextInput from 'components/shared/StyledTextInput/StyledTextInput';
import { OrderTable } from 'components/SupplierPanel';
import { useErrorDescriptions } from 'constants/errorDescriptions';
import { useOrderStatuses } from 'constants/orderStatuses';
import { PAGINATION_SIZE } from 'constants/pagination';
import { CLIENT_PLACE_ORDER } from 'constants/routes';
import { useAuth } from 'context/auth/AuthContextProvider';
import WithAuth from 'hoc/withAuth';
import { BannerData, ClientOrdersTableItem } from 'interfaces/commonInterfacesAndTypes';
import { HTTPService } from 'service';
import { orderDTOToClientOrdersTableItemMapper } from 'service/http/mappers';
import { Banner } from 'storybook';
import { errorHandler } from 'utils/errorHandler';

import styles from './OrdersPage.module.css';

const OrdersPage = () => {
   const navigate = useNavigate();
   const { user } = useAuth();
   const { t } = useTranslation();
   const [orders, setOrders] = useState<ClientOrdersTableItem[]>([]);
   const [ordersTotalCount, setOrdersTotalCount] = useState(0);
   const [isPendingOrdersShipmentAlertVisible, setIsPendingOrdersShipmentAlertVisible] =
      useState(false);
   const [page, setPage] = useState(0);
   const [orderIdFilter, setOrderIdFilter] = useState<string>();
   const [debouncedOrderIdFilter] = useDebouncedValue(orderIdFilter, 200);
   const [isOrdersFirstFetchLoading, setIsOrdersFirstFetchLoading] = useState(false);
   const [bannerData, setBannerData] = useState<BannerData | null>(null);
   const tableContainer = useRef<HTMLDivElement>(null);

   const { orderStatusesLegend } = useOrderStatuses();

   const currentMonth = DateTime.now().setLocale('pl-PL').monthLong;

   const { DEFAULT_ERROR_DESCRIPTIONS } = useErrorDescriptions();

   const handleFetchOrdersError = useCallback(
      (error: unknown) =>
         errorHandler(error, () =>
            setBannerData({
               variant: 'error',
               description: DEFAULT_ERROR_DESCRIPTIONS.FETCH_DATA,
            }),
         ),
      [DEFAULT_ERROR_DESCRIPTIONS.FETCH_DATA],
   );

   const fetchOrders = useCallback(
      (pageNumber: number, firstFetch?: boolean) => {
         firstFetch && setIsOrdersFirstFetchLoading(true);
         HTTPService.getUserOrderList(
            { page: pageNumber, size: PAGINATION_SIZE },
            debouncedOrderIdFilter,
         )
            .then(({ data: orderListData }) => {
               if (firstFetch) {
                  setOrdersTotalCount(orderListData.totalElements);
                  setIsPendingOrdersShipmentAlertVisible(orderListData.ordersAlert);
               }
               const orderListItems = orderListData.orders.map(
                  orderDTOToClientOrdersTableItemMapper,
               );
               firstFetch
                  ? setOrders(orderListItems)
                  : setOrders(prevOrders => prevOrders.concat(orderListItems));
            })
            .catch(handleFetchOrdersError)
            .finally(() => firstFetch && setIsOrdersFirstFetchLoading(false));
      },
      [debouncedOrderIdFilter, handleFetchOrdersError],
   );

   useEffect(() => {
      fetchOrders(0, true);
      setPage(1);
      tableContainer.current && (tableContainer.current.scrollTop = 0);
   }, [fetchOrders]);

   const handleLoadNextOrders = () => {
      fetchOrders(page);
      setPage(prevPage => prevPage + 1);
   };

   const isClientCanPlaceOrder = user?.suspension.blocked === false;
   const withAlertBanners =
      isPendingOrdersShipmentAlertVisible || user?.suspension.blocked || bannerData;

   return (
      <div className={styles.mainContainer}>
         <div className={styles.headerContainer}>
            <div
               className={classNames(styles.bannersContainer, {
                  [styles.withAlertBanners]: withAlertBanners,
               })}
            >
               {user?.suspension.blocked && (
                  <Banner
                     children={
                        <>
                           <p>{t('client_orders.place_order_blocked')}</p>
                           <p>{user?.suspension.reason}</p>
                        </>
                     }
                     variant="error"
                     icon={<Ban />}
                     fullWidth
                     className={classNames(styles.banner, styles.suspensionInfoBanner)}
                  />
               )}
               {isPendingOrdersShipmentAlertVisible && (
                  <Banner
                     children={t('client_orders.alert_pending_shipments', { month: currentMonth })}
                     variant="error"
                     fullWidth
                     className={styles.banner}
                  />
               )}
               {bannerData && (
                  <Banner
                     variant={bannerData.variant}
                     title={bannerData.title}
                     children={bannerData.description}
                     className={styles.banner}
                     onClose={() => setBannerData(null)}
                     withCloseIcon
                     fullWidth
                  />
               )}
               <Banner
                  fullWidth
                  variant="info"
                  children={t('client_orders.mobile_scroll_info')}
                  className={classNames(styles.banner, styles.mobileInfoBanner)}
               />
            </div>
            <div className={styles.header}>
               <div>
                  <StyledTextInput
                     type="text"
                     onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                        setOrderIdFilter(event.target.value)
                     }
                     value={orderIdFilter}
                     placeholder={t('client_orders.search_placeholder')}
                     leftIcon={<Search />}
                     className={styles.searchInput}
                  />
                  <StyledButton
                     variant="filled-primary"
                     text={t('client_orders.place_order')}
                     onClick={() => navigate(CLIENT_PLACE_ORDER)}
                     disabled={!isClientCanPlaceOrder}
                     className={styles.searchBtn}
                  />
               </div>
               <StatusLegend statusDataItems={orderStatusesLegend} />
            </div>
         </div>
         <CustomInfiniteScroll
            containerId="orderTableInfiniteScrollContainer"
            containerRef={tableContainer}
            next={handleLoadNextOrders}
            dataLength={orders.length}
            hasMore={orders.length !== ordersTotalCount}
            isInitialLoading={isOrdersFirstFetchLoading}
            noItemsNotificationElement={
               <NoItemsNotificationWithLink
                  icon={<PalletsStackIcon />}
                  desctiption={t('client_orders.no_orders')}
                  linkText={t('client_orders.place_order')}
                  redirectTo={CLIENT_PLACE_ORDER}
                  isLinkDisabled={!isClientCanPlaceOrder}
               />
            }
         >
            <OrderTable
               mode="CLIENT_TABLE"
               orders={orders}
               className={classNames({
                  [styles.tableHideMobile]: !orders.length,
               })}
            />
         </CustomInfiniteScroll>
      </div>
   );
};

export default WithAuth(OrdersPage, 'ROLE_CLIENT');
